import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useSetUpMap } from "../../../../App/useSetUpMap";
import { MapContext } from '../../../../App/MapContextContainer';
import PopupTemplate from '@arcgis/core/PopupTemplate';
import MapContainer from '../../../../components/Maps/MapContainer';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import SimpleRenderer from '@arcgis/core/renderers/SimpleRenderer';
import { DECLARATIONS_MIN_SCALE_TO_SHOW, useNotificationsLayer } from '../../../../App/useNotificationsLayer';
import { HabitatsContext } from '../../HabitatsContextContainer';
import { AppSettingsContext } from '../../../../App/AppSettingsContextContainer';
import WFSLayer from '@arcgis/core/layers/WFSLayer';
import useBboxUpdate from '../../../ForestsNearUs/components/Map/useBboxUpdate';
import useGetHabitatPopupContent from './useGetHabitatPopupContent';
import { whenFalse } from '@arcgis/core/core/watchUtils';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import { PREFERRED_WKID } from '../../../CustomAreas/UserDataContextContainer';
import Legend from "@arcgis/core/widgets/Legend.js";
import Expand from '@arcgis/core/widgets/Expand';
import { cuttingRealizationPracticesEsriSQLReducer, getFromAndToDatesFromMonths, makeCuttingRealizationPracticePropQueryable } from '../../../utils';
import useGridRenderer from '../../../../components/Maps/gridRenderer';
import Polygon from '@arcgis/core/geometry/Polygon';
import { concatenateNumbers } from '../../../Observations/components/Map/concatenateStrings';
import { PageContext } from '../../../../App/PageContextContainer';
import loadingNotifications from '../../../../App/loadingNotifications';
import useTestLayer from '../../../../App/useTestLayer';
import { useTranslation } from 'react-i18next';
import errors from '../../../../App/errors';

const MIN_SCALE_TO_SHOW_CLASHES = 150000;
const habitatsLayerUrl = "https://avoin.metsakeskus.fi/rajapinnat/v1/habitat/ows";

export default function HabitatsMap() {
    const elementRef = useRef(null);
    const { webmap, view } = useSetUpMap(elementRef, "6e34c9c7b08d41779b23bf1241649275");
    const mapContext = useContext(MapContext);
    useNotificationsLayer(webmap, view, new Date(2020, 3, 1));
    const habitatsContext = useContext(HabitatsContext);
    const { appliedFilters } = useContext(AppSettingsContext);
    const getPopupContent = useGetHabitatPopupContent();
    const [fetchedMapPages, setFetchedMapPages] = useState<string[]>([]);
    const { grid25kmGeojsonLayer, visibleGrid25kmGeojsonLayer } = useGridRenderer(
        [0, 10, 25, 50, 100, 150],
        "Metsänkäyttöilmoitukset tärkeissä elinympäristöissä per 25km karttalehti");
    const pageContext = useContext(PageContext);
    const viewWatcherRef = useRef<IHandle>();
    const { t, i18n } = useTranslation("habitats");

    const clashingSymbol = new SimpleMarkerSymbol({
        color: "red",
        size: "8"
    });

    const pointsRenderer = new SimpleRenderer({
        symbol: clashingSymbol
    });

    const pointsObservationsFeatureLayer = useMemo(() => new FeatureLayer({
        source: [],
        renderer: pointsRenderer,
        title: "Metsänkäyttöilmoitukset tärkeissä elinympäristöissä",
        labelsVisible: false,
        minScale: MIN_SCALE_TO_SHOW_CLASHES,
        outFields: ["*"],
        objectIdField: "id",
        legendEnabled: false,
        popupEnabled: false,
        spatialReference: { wkid: 4326 },
        geometryType: "point",
        fields: [{
            name: "cuttingRealizationPractices",
            type: "string"
        },
        {
            name: "SPECIALFEATURECODE",
            type: "integer"
        },
        {
            name: "declarationArrivalDate",
            type: "string"
        }]
    }), []);

    const habitatLayer = useMemo(() => new WFSLayer({
        url: habitatsLayerUrl,
        title: "Elinympäristöt",
        popupEnabled: true,
        legendEnabled: false,
        visible: true,
        copyright: "Suomen metsäkeskus",
        minScale: DECLARATIONS_MIN_SCALE_TO_SHOW,
        spatialReference: { wkid: 4326 },
        customParameters: {
            cql_filter: "1 = 0"
        }
    }), []);

    useEffect(() => {
        if (habitatLayer) {
            habitatLayer.title = t("elinympäristötKerrosOtsikko");
        }

        if (pointsObservationsFeatureLayer) {
            pointsObservationsFeatureLayer.title = t("mkiElinympäristöissäKerrosOtsikko");
        }

        if (visibleGrid25kmGeojsonLayer) {
            visibleGrid25kmGeojsonLayer.title = t("mkiElinympäristöissäPerKarttaruutuKerrosOtsikko");
        }
    }, [i18n.language, t]);

    const standLayerBboxUpdate = useBboxUpdate(view, habitatLayer, {
    }, "", "GEOMETRY");

    useTestLayer(habitatsLayerUrl + "?SERVICE=WFS&REQUEST=GetCapabilities&VERSION=2.0.0&format=json", { ...errors.layerLoadingFailed, replacementValues: ["elinympäristöt"] });

    useEffect(() => {
        const asyncCall = async () => {
            if (mapContext.mapAndViewReady && habitatsContext.habitatsState.isInitialized) {
                try {
                    pageContext.setLoadingSomething(loadingNotifications.loadingHabitatsGrid);

                    let crpQuery = "";
                    let specialFeatureCodeQuery = "";
                    let declarationFromDateQuery = "";
                    let declarationToDateQuery = "";

                    if (habitatsContext.habitatsState.cuttingRealizationPractices?.length > 0) {
                        crpQuery = habitatsContext.habitatsState.cuttingRealizationPractices.reduce((prev, curr) => {
                            return (prev ? prev + "." + curr : curr);
                        }, "");
                    }

                    if (habitatsContext.habitatsState.specialFeatureCodes?.length > 0) {
                        specialFeatureCodeQuery = habitatsContext.habitatsState.specialFeatureCodes.reduce((prev, curr) => {
                            return (prev ? prev + "." + curr : curr);
                        }, "");
                    }

                    if (habitatsContext.habitatsState.declarationArrivalMonths.length > 0) {
                        const { fromDate, toDate } = getFromAndToDatesFromMonths(habitatsContext.habitatsState.declarationArrivalMonths);

                        if (fromDate && toDate) {
                            declarationFromDateQuery = `${fromDate.getFullYear()}-${(fromDate.getMonth() + 1 + "").padStart(2, "0")}-01`;
                            declarationToDateQuery = `${toDate.getFullYear()}-${(toDate.getMonth() + 1 + "").padStart(2, "0")}-${toDate.getDate()}`;
                        }
                    }

                    const responseGrid = await fetch(`${process.env.REACT_APP_API_URL}/api/Get25KmGrid?crp=${crpQuery}` +
                        `&specialFeatureCodes=${specialFeatureCodeQuery}` +
                        `&declarationArrivalDateFrom=${declarationFromDateQuery}` +
                        `&declarationArrivalDateTo=${declarationToDateQuery}`);

                    const gridData = await responseGrid.json();

                    const bufferedGraphics: Graphic[] = [];

                    for (const feature of gridData.mapPages.features) {
                        const g = new Graphic({
                            attributes: feature.properties,
                        });

                        const p: Polygon = new Polygon({
                            rings: feature.geometry.coordinates[0],
                            spatialReference: { wkid: PREFERRED_WKID },
                            hasZ: false
                        });

                        g.geometry = p;

                        bufferedGraphics.push(g);
                    }

                    const queryArea = grid25kmGeojsonLayer.createQuery();
                    const response = await grid25kmGeojsonLayer.queryFeatures(queryArea);

                    const addEdits = {
                        deleteFeatures: response.features,
                        addFeatures: bufferedGraphics
                    };

                    await grid25kmGeojsonLayer.applyEdits(addEdits);

                    await visibleGrid25kmGeojsonLayer.applyEdits(addEdits);
                } catch (error) {
                    pageContext.setMapError(errors.loadingHabitatsGridFailed);
                }
                finally {
                    pageContext.resetLoadingSomething(loadingNotifications.loadingHabitatsGrid);
                }
            }
        };

        asyncCall();
    }, [mapContext.mapAndViewReady, habitatsContext.habitatsState.cuttingRealizationPractices,
    habitatsContext.habitatsState.declarationArrivalMonths,
    habitatsContext.habitatsState.specialFeatureCodes, habitatsContext.habitatsState.isInitialized]);

    useEffect(() => {
        if (habitatLayer.loaded && habitatsContext.habitatsState.specialFeatureCodesForDropDown) {
            habitatLayer.popupTemplate = new PopupTemplate({
                title: t("elinympäristötPopupOtsikko"),
                outFields: ["*"],
                content: getPopupContent
            });
        }
    }, [i18n.language, habitatLayer.loaded, habitatsContext.habitatsState, habitatsContext.habitatsState.specialFeatureCodesForDropDown]);

    const legendExpand = useMemo(() => {
        let legend = new Legend({
            view: view,
            basemapLegendVisible: false,
            label: "Määrä",
            visible: true
        });

        return new Expand({
            expandIconClass: "esri-icon-legend",
            view: view,
            expanded: false,
            content: legend
        });
    }, [view]);

    useEffect(() => {
        if (viewWatcherRef.current) {
            viewWatcherRef.current.remove();
        }

        viewWatcherRef.current = view.watch("zoom", () => {
            if (view.zoom > 11) {
                legendExpand.visible = false;
            }
            else {
                legendExpand.visible = true;
            }
        });

        return () => viewWatcherRef.current?.remove();
    }, [legendExpand, view]);

    useEffect(() => {
        const asyncCall = async () => {
            if (mapContext.mapAndViewReady) {
                webmap.add(habitatLayer);

                webmap.add(grid25kmGeojsonLayer);
                webmap.add(visibleGrid25kmGeojsonLayer);
                webmap.add(pointsObservationsFeatureLayer);

                view.ui.add(legendExpand, "bottom-left");

                view.whenLayerView(grid25kmGeojsonLayer).then((lView) => {
                    whenFalse(lView, "updating", async () => {
                        const queryArea = grid25kmGeojsonLayer.createQuery();

                        queryArea.geometry = view.extent;

                        const response = await grid25kmGeojsonLayer.queryFeatures(queryArea);

                        if (view.zoom >= 10) {
                            let mapPagesConcatenated = "";

                            response.features.forEach(f => {
                                if (fetchedMapPages.indexOf(f.attributes.LEHTITUNNU) < 0) {
                                    fetchedMapPages.push(f.attributes.LEHTITUNNU);

                                    mapPagesConcatenated = mapPagesConcatenated + "," + f.attributes.LEHTITUNNU;
                                }
                            });

                            if (mapPagesConcatenated.length > 0) {
                                try {
                                    pageContext.setLoadingSomething(loadingNotifications.loadingHabitatsClashes);

                                    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/GetHabitatClashes/${mapPagesConcatenated}`);
                                    const clashData = await response.json();
                                    const bufferedGraphics: Graphic[] = [];

                                    for (const feature of clashData) {
                                        const p: Point = new Point({
                                            latitude: feature.geometry.coordinates[1],
                                            longitude: feature.geometry.coordinates[0],
                                            spatialReference: { wkid: PREFERRED_WKID }
                                        });

                                        const modifiedFeature = makeCuttingRealizationPracticePropQueryable(feature);

                                        const g = new Graphic({
                                            attributes: modifiedFeature.properties,
                                            symbol: clashingSymbol,
                                            geometry: p
                                        });

                                        bufferedGraphics.push(g);
                                    }

                                    const addEdits = {
                                        addFeatures: bufferedGraphics
                                    };

                                    await pointsObservationsFeatureLayer.applyEdits(addEdits);
                                } catch (error) {
                                    pageContext.setMapError(errors.loadingHabitatsForMapPageFailed);
                                }
                                finally {
                                    pageContext.resetLoadingSomething(loadingNotifications.loadingHabitatsClashes);
                                }
                            }
                        }
                    });
                });
            }
        }

        asyncCall();
    }, [mapContext.mapAndViewReady, webmap]);

    useEffect(() => {
        if (mapContext.mapAndViewReady === false || habitatsContext.habitatsState === undefined) {
            return;
        }

        let definitionExpression = "";

        if (habitatsContext.habitatsState.cuttingRealizationPractices?.length > 0) {
            if (definitionExpression !== "") {
                definitionExpression += " AND ";
            }

            const cuttingRealizationPracticeSQL =
                habitatsContext.habitatsState.cuttingRealizationPractices.reduce(cuttingRealizationPracticesEsriSQLReducer, "");

            definitionExpression = definitionExpression + "(" + cuttingRealizationPracticeSQL + ")";
        }

        if (habitatsContext.habitatsState.specialFeatureCodes?.length > 0) {
            if (definitionExpression !== "") {
                definitionExpression += " AND ";
            }

            definitionExpression = definitionExpression + `SPECIALFEATURECODE IN(${concatenateNumbers(habitatsContext.habitatsState.specialFeatureCodes)})`;
        }

        if (habitatsContext.habitatsState.declarationArrivalMonths.length > 0) {
            if (definitionExpression !== "") {
                definitionExpression += " AND ";
            }

            const { fromDate, toDate } = getFromAndToDatesFromMonths(habitatsContext.habitatsState.declarationArrivalMonths);

            if (fromDate && toDate) {
                definitionExpression = definitionExpression + `declarationArrivalDate >= '${fromDate.getFullYear()}-${(fromDate.getMonth() + 1 + "").padStart(2, "0")}-01' AND `;
                definitionExpression = definitionExpression + `declarationArrivalDate <= '${toDate.getFullYear()}-${(toDate.getMonth() + 1 + "").padStart(2, "0")}-${toDate.getDate()}'`;
            }
        }

        pointsObservationsFeatureLayer.definitionExpression = definitionExpression;
    }, [mapContext.mapAndViewReady, habitatsContext.habitatsState, appliedFilters]);

    return <MapContainer elementRef={elementRef} view={view}></MapContainer>
}
